import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

export const RevenueVsAdSpendGrowthChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        let cumulativeRevenue = 0;
        let cumulativeAdSpend = 0;

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                cumulativeRevenue += metrics?.revenue ?? 0;
                cumulativeAdSpend += metrics?.adSpend ?? 0;

                return {
                    date,
                    revenue: Math.round(cumulativeRevenue * 100) / 100,
                    adSpend: Math.round(cumulativeAdSpend * 100) / 100,
                };
            });
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(value, currencySymbol),
                                name === 'revenue'
                                    ? t('charts.cumulativeRevenue', 'Cumulative Revenue')
                                    : t('charts.cumulativeAdSpend', 'Cumulative Ad Spend'),
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <Legend
                            verticalAlign="bottom"
                            iconType="circle"
                            wrapperStyle={{ fontSize: '10px' }}
                            formatter={(value: string) =>
                                value === 'revenue'
                                    ? t('charts.revenue', 'Revenue')
                                    : t('charts.adSpend', 'Ad Spend')
                            }
                        />
                        <Line
                            type="monotone"
                            dataKey="revenue"
                            stroke={CHART_COLORS.revenue}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.revenue }}
                        />
                        <Line
                            type="monotone"
                            dataKey="adSpend"
                            stroke={CHART_COLORS.expense}
                            strokeWidth={CHART_STYLES.line.strokeWidth}
                            dot={CHART_STYLES.line.dot}
                            activeDot={{ ...CHART_STYLES.line.activeDot, fill: CHART_COLORS.expense }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
